import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import type { AccessLevel } from '@yawi/types';
import { useAccessLevels } from '../hooks/useAccessLevels';

interface AccessLevelSelectorProps {
  integrationId: string;
}

const COOLDOWN_MS = 60_000;

function useCooldownSeconds(refreshedAt: number | null): number {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (refreshedAt === null) {
      setRemaining(0);
      return;
    }
    const tick = () => {
      const diff = Math.ceil((COOLDOWN_MS - (Date.now() - refreshedAt)) / 1000);
      setRemaining(diff > 0 ? diff : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [refreshedAt]);

  return remaining;
}

interface GroupSectionProps {
  title: string;
  items: AccessLevel[];
  updating: boolean;
  onSelect: (id: string) => void;
}

const GroupSection: React.FC<GroupSectionProps> = ({ title, items, updating, onSelect }) => {
  if (items.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{title}</p>
      {items.map((al) => (
        <label
          key={al.id}
          className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
            al.isDefault
              ? 'border-primary bg-primary/5'
              : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
          } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input
            type="radio"
            name="default-access-level"
            value={al.id}
            checked={al.isDefault}
            disabled={updating}
            onChange={() => onSelect(al.id)}
            className="accent-primary"
          />
          <span className="flex-1 text-sm text-slate-700">{al.name}</span>
          {al.isDefault && (
            <span className="flex items-center gap-1 text-xs font-medium text-green-600">
              <CheckCircle size={12} />
              Predeterminado
            </span>
          )}
        </label>
      ))}
    </div>
  );
};

export const AccessLevelSelector: React.FC<AccessLevelSelectorProps> = ({ integrationId }) => {
  const { accessLevels, loading, updating, error, refreshedAt, refresh, setDefault, reload } =
    useAccessLevels(integrationId);
  const cooldownSeconds = useCooldownSeconds(refreshedAt);
  const cooldownActive = cooldownSeconds > 0;

  const visitorLevels = accessLevels.filter((al) => al.type === 'visitor');
  const employeeLevels = accessLevels.filter((al) => al.type === 'employee');

  return (
    <div className="mt-5 pt-4 border-t border-slate-100 space-y-3">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold text-slate-700">Nivel de acceso predeterminado</h5>
        <button
          onClick={refresh}
          disabled={loading || updating || cooldownActive}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refrescar
          {cooldownActive && <span className="text-slate-400">({cooldownSeconds}s)</span>}
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Loader2 size={20} className="animate-spin text-slate-400" />
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>{error}</p>
            <button
              onClick={reload}
              className="mt-1 text-xs underline hover:no-underline"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {!loading && !error && accessLevels.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-3">
          No hay niveles de acceso configurados en HikCentral.
        </p>
      )}

      {!loading && !error && accessLevels.length > 0 && (
        <div className="space-y-4">
          <GroupSection
            title="Visitantes"
            items={visitorLevels}
            updating={updating}
            onSelect={setDefault}
          />
          <GroupSection
            title="Empleados"
            items={employeeLevels}
            updating={updating}
            onSelect={setDefault}
          />
        </div>
      )}
    </div>
  );
};
